import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ControlValueAccessor, UntypedFormArray, UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map, Observable, startWith, Subject } from 'rxjs';
import { CustomIcons } from '@shared/models/custom-icons';
import { MultiSelectOption } from '@shared/components/multi-select/models';
import { isEqual } from 'lodash';
import { ServerLinks } from '@shared/services/server-links';
import { ServerLinkName } from '@shared/models/server-links-model';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: '`app-multi-select-with-input',
  templateUrl: './multi-select-with-input.component.html',
  styleUrls: ['./multi-select-with-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => MultiSelectWithInputComponent),
      multi: true
    }
  ]
})
export class MultiSelectWithInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private _value: Array<string | number> = [];
  private _options: MultiSelectOption[] = [];
  private componentDestroy$ = new Subject<void>();

  public get options(): MultiSelectOption[] {
    return this._options;
  }

  @Input()
  public set options(value: MultiSelectOption[]) {
    if (value && value.length > 0) this.isCustomIconsDropdown = 'icon' in value[0];

    if (!isEqual(value, this.options)) {
      this._options = value;
      this.filteredMultipleOptions = value;
      this.optionsFormArray.clear();
      this.optionTitles = this.options?.map(option => option.title);
      setTimeout(() => {
        this._options?.forEach((option, index) => {
          this.optionsFormArray.setControl(index, new UntypedFormControl(option.isSelected), { emitEvent: false });
        });
        this.cdr.detectChanges();
      });
    }
  }

  public get value(): Array<string | number> {
    return this._value;
  }

  @Input()
  public set value(value: Array<string | number>) {
    if (!value) {
      this.optionsFormArray.reset([], { emitEvent: false });
    } else {
      this.options.forEach((option, i) => {
        if (value.includes(option.value)) {
          option.isSelected = true;
          this.optionsFormArray.controls[i]?.setValue(true);
        } else {
          option.isSelected = false;
          this.optionsFormArray.controls[i]?.setValue(false);
        }
      });
    }
    if (!value || value.length === 0) {
      this.chooseAll = false;
    }

    this._value = value;
    this.onTouch(this._value);
    this.cdr.detectChanges();
  }
  @Input() public isGrayGradient = false;
  @Input() public chooseAllText = 'DATA.TABLE_FILTER.ALL';
  // передается строка из ru-ru.son/en-us.json
  @Input() public floatingTitle = '';
  // передается строка без перевода (используется для данных из объекта НД)
  @Input() public floatingRuTitle = '';
  @Input() public dropdownWidth: number | undefined;
  @Input() public showChooseAllBtn = true;
  @Input() public maxDropdownHeight = 200;
  @Input() public optionHeight = 48;
  @Input() public isOpen = false;
  @Input() public isSearchFilter = false;
  @Input() public hideInput = false;
  @Input() public emitValue = true;

  @Input()
  @HostBinding('class.flat-layout')
  public flatLayout = false;

  @Output() public readonly choose = new EventEmitter<boolean[]>();
  @Output() public readonly dismiss = new EventEmitter<void>();

  @HostBinding('class.placeholder-raised') public placeholderRaised = false;
  @HostBinding('class.focused') public isFocused = false;

  @HostBinding('class.has-value')
  public get hasValue(): boolean {
    return this.optionsFormArray.value.some((isChosen: boolean) => isChosen) || !!this.searchPhrase.value;
  }

  public chooseAll = false;
  public selectForm = new UntypedFormGroup({
    options: new UntypedFormArray([])
  });

  public optionsFormArray = this.selectForm.get('options') as UntypedFormArray;
  public placeholder = '';
  public icons = CustomIcons;
  public searchPhrase = new UntypedFormControl('');
  public optionTitles: string[] = [];
  public isCustomIconsDropdown = false;
  public fileLink: string;
  public filteredMultipleOptions: MultiSelectOption[] = [];
  public filteredOptions$ = (this.searchPhrase.valueChanges as Observable<string>).pipe(
    startWith(''),
    map((value: string) => {
      if (value === '') return this.optionTitles;
      this.placeholderRaised = !!value;

      return this.optionTitles.filter(title => title.toLowerCase().includes(value.toLowerCase()));
    })
  );

  public get selectState(): string {
    const chosenAmount = this.optionsFormArray.value.reduce((acc: number, isChosen: boolean) => {
      if (isChosen) return acc + 1;
      return acc;
    }, 0);

    return chosenAmount > 0 ? `${this.translateService.instant('OTHER.SELECTED')}: ${chosenAmount}` : this.placeholder;
  }

  public get isIndeterminate(): boolean {
    return this.chooseAll ? false : this.optionsFormArray.value.some((isChosen: boolean) => isChosen);
  }

  get selectedMultipleOptionIndexes(): number[] {
    return this.optionsFormArray.value?.reduce((arr: number[], value: boolean, index: number) => {
      if (value) { arr.push(index); }
      return arr;
    }, []);
  }

  constructor(private cdr: ChangeDetectorRef, serverLinks: ServerLinks, private translateService: TranslateService) {
    this.fileLink = serverLinks.getLink(ServerLinkName.DownloadFile);
  }

  private onChange: any = (): void => {
    //
  };

  private onTouch: any = (): void => {
    //
  };

  private getSelectedOptionsFromValue(value: boolean[]): Array<string | number> {
    return value.reduce((acc: Array<string | number>, isChosen: boolean, index: number) => {
      if (isChosen) acc.push(this.options[index].value);
      return acc;
    }, []);
  }

  public ngOnInit(): void {
    if (this.optionsFormArray.length === 0) {
      this._options.forEach(op => {
        this.optionsFormArray.push(new UntypedFormControl(op.isSelected));
      });
    }

    (this.searchPhrase.valueChanges as Observable<string>)
      .pipe(takeUntil(this.componentDestroy$))
      .subscribe((search: string) => {
        if (search === '') {
          this.filteredMultipleOptions = this.options;
          return;
        }
        this.placeholderRaised = !!search;
        this.filteredMultipleOptions = this.options.filter(
          option => option.title.toLowerCase().includes(search.toLowerCase())
        );
      });
  }

  ngOnDestroy() {
    this.componentDestroy$.next();
    this.componentDestroy$.complete();
  }

  public checkOption(isClearAll = false): void {
    const value = this.optionsFormArray.value as boolean[];

    this._options = this._options.map((val, i) => ({ ...val, isSelected: value[i] }));
    if (!this.isCustomIconsDropdown && value.length > 0) this.chooseAll = value.every((isChosen: boolean) => isChosen);
    this.placeholderRaised = this.selectState !== '';

    if (this.emitValue || isClearAll) {
      const selectedOptions = this.getSelectedOptionsFromValue(value);
      this.writeValue(selectedOptions);
      this.onChange(this._value);
    }
  }

  public setOptionsEqualValue(isChosen: boolean): void {
    if (this.filteredMultipleOptions.length === this.options.length) {
      this.optionsFormArray.setValue(Array(this.options?.length).fill(isChosen));
    } else {
      const filteredChangedValues = this.filteredMultipleOptions.map(option => option.value);
      const newValue = this.options.map((option, index) =>
        filteredChangedValues.includes(option.value)
          ? isChosen
          : this.optionsFormArray.value[index]
      );
      this.optionsFormArray.setValue(newValue);
    }
  }

  public clearAll(event: Event | null = null): void {
    if (event) {
      event.stopPropagation();
    }

    this.optionsFormArray.setValue(Array(this.options?.length).fill(false));
    this.searchPhrase.setValue('');
    this.checkOption(true);
  }

  public onFocusIn(): void {
    this.placeholderRaised = true;
    this.isOpen = true;
    this.isFocused = true;
  }

  public onFocusOut(): void {
    this.isFocused = false;
    if (!this.searchPhrase.value) this.placeholderRaised = false;
  }

  public setAllChosen(isShow: boolean): void {
    this.chooseAll = isShow;
    this.setOptionsEqualValue(isShow);
    this.checkOption();
  }

  public onBackdropClick(): void {
    this.searchPhrase.setValue('');
    this.isOpen = false;
  }

  public triggerWriteValue(): void {
    this.writeValue(this.getSelectedOptionsFromValue(this.optionsFormArray.value as boolean[]));
    this.onChange(this._value);
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  public writeValue(value: Array<string | number>): void {
    this.value = value;
  }

  changeMultipleSelection(options: ({ index: number; isSelected: boolean })[]) {
    const updatedFormArray = Array(options?.length).fill(false);
    const selectedOptions = options.filter(option => option.isSelected);
    const updatedValue: string[] = [];
    selectedOptions.forEach(option => {
      updatedFormArray[option.index] = true;
      this.options[option.index] = {
        ...this.options[option.index],
        isSelected: true,
      };
      updatedValue.push(this.options[option.index].value as string);
    });
    this.options = [...this.options];
    this.optionsFormArray.setValue(updatedFormArray);
    this.writeValue(updatedValue);
    this.onChange(this._value);
    this.cdr.detectChanges();
  }
}
